import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
  MIN_TERM_LENGTH,
  SEARCH_QUERY_DELAY,
  URL_FILTERS_SEARCH_NAME,
  URL_QUERY_SEARCH_NAME,
} from '@maison/helpers/search-helper';
import debounce from 'lodash-es/debounce';

export const useSearchPanel = () => {
  const store = useStore();

  const autocompleteRef = ref(null);
  const searchInitialized = ref(false); // late initialization of autocomplete to avoid teleport issue
  const stagedQuery = ref('');
  const urlQueryTerm = ref('');

  const currentQuery = computed(() => store.state.search.query);

  const isQueryResultEmpty = computed(
    () =>
      !store.state.search.isLoading &&
      stagedQuery.value.length >= MIN_TERM_LENGTH &&
      stagedQuery.value === currentQuery.value &&
      store.getters['search/totalResults'] === 0,
  );

  const hasEmptyState = computed(() => stagedQuery.value.length < MIN_TERM_LENGTH || isQueryResultEmpty.value);

  const resetUrlParams = (query = '') => {
    const url = new URL(window.location);

    if (!query || query.length < MIN_TERM_LENGTH) {
      url.searchParams.delete(URL_QUERY_SEARCH_NAME);
    } else {
      url.searchParams.set(URL_QUERY_SEARCH_NAME, query);
    }

    const keysToDelete = [];
    for (const [key] of url.searchParams.entries()) {
      if (key === URL_QUERY_SEARCH_NAME) {
        continue;
      }

      if (key.startsWith(`${URL_FILTERS_SEARCH_NAME}.`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => url.searchParams.delete(key));

    window.history.replaceState(null, null, url);
  };

  const applyQuery = async (term, updateUrl = true) => {
    stagedQuery.value = term;

    if (updateUrl) {
      resetUrlParams(term);
    }

    if (term.length < MIN_TERM_LENGTH) {
      return;
    }

    await store.dispatch('search/setQuery', term);
  };

  const setQueryDebounced = debounce(applyQuery, SEARCH_QUERY_DELAY);

  const onChange = async (option) => {
    if (!option && option.value) {
      return;
    }

    // prevent loading on url query set
    if (option.value === urlQueryTerm.value) {
      return;
    }

    await applyQuery(option.value);
  };

  const setQueryFromUrl = async () => {
    urlQueryTerm.value = new URLSearchParams(window.location.search).get(URL_QUERY_SEARCH_NAME);
    if (urlQueryTerm.value) {
      await applyQuery(urlQueryTerm.value, false);
    }
  };

  const setCustomQuery = (query) => {
    if (!autocompleteRef.value) {
      return;
    }

    autocompleteRef.value.setValue(query);
  };

  const unwatch = watch([searchInitialized, autocompleteRef], async () => {
    if (!searchInitialized.value || !autocompleteRef.value) {
      return;
    }

    if (urlQueryTerm.value) {
      setCustomQuery(urlQueryTerm.value);
      await applyQuery(urlQueryTerm.value, false);
      urlQueryTerm.value = null; // reset query after first load
    }

    unwatch();
  });

  return {
    applyQuery,
    autocompleteRef,
    currentQuery,
    hasEmptyState,
    isQueryResultEmpty,
    onChange,
    searchInitialized,
    setCustomQuery,
    setQueryDebounced,
    setQueryFromUrl,
    stagedQuery,
    urlQueryTerm,
  };
};
